/**
 * TOTPInput — Saisie de code TOTP à 6 chiffres
 *
 * 6 champs individuels avec :
 * - Auto-focus sur le champ suivant après saisie
 * - Retour au champ précédent avec Backspace
 * - Collage automatique d'un code à 6 chiffres
 * - Support clavier numérique mobile (inputMode="numeric")
 */

import { useRef, useCallback, type KeyboardEvent, type ClipboardEvent } from 'react';

interface TOTPInputProps {
  /** Valeur courante (chaîne de 0 à 6 chiffres) */
  value: string;
  /** Appelé à chaque modification */
  onChange: (value: string) => void;
  /** Désactiver les champs */
  disabled?: boolean;
  /** Focus automatique au montage */
  autoFocus?: boolean;
}

const CODE_LENGTH = 6;

export function TOTPInput({ value, onChange, disabled = false, autoFocus = false }: TOTPInputProps) {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  /** Transforme la valeur en tableau de 6 caractères */
  const digits = Array.from({ length: CODE_LENGTH }, (_, i) => value[i] ?? '');

  /** Met à jour un chiffre à la position donnée */
  const setDigit = useCallback(
    (index: number, digit: string) => {
      const newDigits = [...digits];
      newDigits[index] = digit;
      const newValue = newDigits.join('').replace(/[^0-9]/g, '');
      onChange(newValue);
    },
    [digits, onChange],
  );

  /** Gère la saisie d'un caractère */
  function handleInput(index: number, inputValue: string) {
    // Ne garder que le dernier chiffre saisi
    const digit = inputValue.replace(/[^0-9]/g, '').slice(-1);
    if (!digit) return;

    setDigit(index, digit);

    // Auto-focus sur le champ suivant
    if (index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  /** Gère les touches spéciales (Backspace, flèches) */
  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        // Effacer le chiffre courant
        setDigit(index, '');
      } else if (index > 0) {
        // Reculer et effacer le champ précédent
        setDigit(index - 1, '');
        inputRefs.current[index - 1]?.focus();
      }
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  /** Gère le collage d'un code complet */
  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, CODE_LENGTH);
    if (pasted.length > 0) {
      onChange(pasted);
      // Focus sur le dernier champ rempli ou le suivant
      const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
      inputRefs.current[focusIndex]?.focus();
    }
  }

  return (
    <div className="flex items-center justify-center gap-2" role="group" aria-label="Code TOTP à 6 chiffres">
      {digits.map((digit, i) => (
        <span key={i} className="flex items-center">
          <input
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]"
            maxLength={1}
            value={digit}
            disabled={disabled}
            autoFocus={autoFocus && i === 0}
            onChange={(e) => handleInput(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            aria-label={`Chiffre ${i + 1} sur 6`}
            className={`
              w-11 h-14 text-center text-2xl font-mono font-bold
              rounded-lg border-2 transition-colors
              focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary
              disabled:opacity-50 disabled:cursor-not-allowed
              ${digit ? 'border-primary bg-primary/5 text-accent' : 'border-gray-300 text-text'}
            `}
          />
          {/* Séparateur au milieu */}
          {i === 2 && <span className="mx-1 text-text-light text-lg font-bold">-</span>}
        </span>
      ))}
    </div>
  );
}
